import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lock, MessageSquare, Bug, Lightbulb, Mail, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  general: <Mail className="h-4 w-4 text-blue-400" />,
  bug: <Bug className="h-4 w-4 text-red-400" />,
  suggestion: <Lightbulb className="h-4 w-4 text-amber-400" />,
  feedback: <MessageSquare className="h-4 w-4 text-green-400" />,
  complaint: <AlertTriangle className="h-4 w-4 text-orange-400" />,
};

const TYPE_COLORS = {
  general: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  bug: 'bg-red-500/15 text-red-400 border-red-500/20',
  suggestion: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  feedback: 'bg-green-500/15 text-green-400 border-green-500/20',
  complaint: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
};

export default function AdminFeedback() {
  const [searchParams] = useSearchParams();
  const keyFromUrl = searchParams.get('key') || '';
  const [key, setKey] = useState(keyFromUrl);
  const [authed, setAuthed] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  const fetchFeedback = async (adminKey, pg = 1) => {
    try {
      const res = await axios.get(`${API}/admin/feedback?key=${encodeURIComponent(adminKey)}&page=${pg}&limit=25`);
      setFeedbacks(res.data.feedbacks);
      setTotal(res.data.total);
      setPage(pg);
      setAuthed(true);
      setError('');
    } catch {
      setError('Invalid key');
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (keyFromUrl) fetchFeedback(keyFromUrl);
  }, [keyFromUrl]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="glass-card p-8 rounded-lg max-w-sm w-full text-center">
          <Lock className="h-10 w-10 text-blue-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-blue-400 font-mono mb-4">Admin Access</h1>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchFeedback(key)}
            placeholder="Enter admin key..."
            className="w-full bg-slate-950/50 border border-white/10 rounded px-4 py-2 text-slate-200 mb-3 outline-none focus:border-blue-500/50"
            data-testid="admin-key-input"
          />
          {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
          <Button onClick={() => fetchFeedback(key)} className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-400/30 font-mono" data-testid="admin-login-btn">
            Access
          </Button>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / 25);

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-blue-400 font-mono">Feedback ({total})</h1>
          <div className="flex items-center gap-3">
            <span className="text-slate-500 text-sm font-mono">Page {page}/{totalPages || 1}</span>
            <Button onClick={() => fetchFeedback(key, page - 1)} disabled={page <= 1} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-300 h-8 w-8 p-0">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button onClick={() => fetchFeedback(key, page + 1)} disabled={page >= totalPages} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-300 h-8 w-8 p-0">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {feedbacks.length === 0 ? (
          <div className="glass-card p-12 rounded-lg text-center">
            <MessageSquare className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 font-mono">No feedback yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {feedbacks.map((f, i) => (
              <div key={f.feedback_id || i} className="glass-card p-4 rounded-lg" data-testid={`feedback-row-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {TYPE_ICONS[f.type] || TYPE_ICONS.general}
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border font-mono ${TYPE_COLORS[f.type] || TYPE_COLORS.general}`}>
                        {(f.type || 'general').toUpperCase()}
                      </span>
                      <span className="text-slate-500 text-xs">{f.user_name}</span>
                      <span className="text-slate-700 text-xs">({f.user_email})</span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">{f.message}</p>
                  </div>
                  <span className="text-slate-600 text-[11px] font-mono flex-shrink-0">
                    {f.created_at ? new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
